import * as React from 'react';

import AppState from '@webapp/store/state';
import { connect } from 'react-redux';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import Flex from '@components/layout/flex';
import Button from '@components/actions/button';
import Typography from '@components/basics/typography';
import SquareAvatar from '@components/basics/square-avatar';

import { uploadImage } from '../../sdk/artists';
import { promiseDelay } from '../../utils/promise-delay';

// import LoadingCtrl from '@webapp/controllers/loading';
import NoAvatar from './no-avatar';
import AvatarLoading from './loading';

interface NewAvatarEditorProps {
  className?: string;
  url?: string;
  smallBtn?: boolean;
  avatarSize?: string;
  isMobile: boolean;
  hasError?: boolean;
  onDrag: (files: File[]) => void;
  onChange: (url: string) => void;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

export const NewAvatarEditor: React.FC<NewAvatarEditorProps> = (props) => {
  const [{ isOver, isDragging }, drag] = useDrop({
    accept: [NativeTypes.FILE],
    drop: (item: any) => props.onDrag(item.files as File[]),
    collect: (monitor) => {
      return {
        isDragging: monitor.canDrop(),
        isOver: monitor.isOver(),
      };
    },
  });

  const propsUrl = props.url;

  const [url, setUrl] = React.useState(propsUrl || '');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => setUrl(propsUrl || ''), [propsUrl]);

  const handleFileChange = async (file: File) => {
    setLoading(true);
    if (file.type.startsWith('image')) {
      const uploadResponse = await uploadImage(file, 'avatar');

      await uploadResponse.upload.request
        .then(() => promiseDelay(2000))
        .then(() => {
          setLoading(false);
          setUrl(uploadResponse.url);
          props.onChange(uploadResponse.url);
        });
    }
  };

  // TODO: Integrate status when uploading
  const getBtnText = (status: any) => {
    switch (status) {
      case 'uploading':
        return <Translate id="UPLOAD.UPLOADING" />;
      case 'done':
        return <Translate id="UPLOAD.DONE" />;
      default:
        return <Translate id="ARTIST_PROFILE.MENU.AVATAR_EDITOR.BUTTON" />;
    }
  };

  return (
    <div className={props.hasError ? props.className : ''}>
      <DropFilesWrapper
        className={`${!props.hasError ? props.className : ''} ${
          isDragging ? 'dragging' : ''
        }`}
        innerRef={drag}
        avatarSize={props.avatarSize}
      >
        <Flex className="wrapper">
          <div className="left">
            <Typography weight="700" className="title">
              <Translate id="ARTIST_PROFILE.MENU.AVATAR_EDITOR.TITLE" />
            </Typography>
            {props.isMobile ? null : (
              <Typography type="small" className="subtitle">
                <Translate id="ARTIST_PROFILE.MENU.AVATAR_EDITOR.SUBTITLE" />
              </Typography>
            )}
            <Button buttonType="secondary-dark" className="btn-browse mt-20">
              <input
                type="file"
                className="file"
                accept="image/*"
                max="1"
                onChange={(ev) =>
                  ev.target.files ? handleFileChange(ev.target.files[0]) : null
                }
              />
              {getBtnText('')}
            </Button>
          </div>
          <AvatarWrapper>
            {url ? <SquareAvatar className="avatar" src={url} /> : <NoAvatar />}
            {/* TODO: Integrate progress of the loading */}
            {loading && <AvatarLoading loading={100} />}
          </AvatarWrapper>
        </Flex>
        {isDragging ? (
          <Flex
            justifyContent="center"
            alignItems="center"
            className={`drag ${isOver ? 'over' : ''}`}
          >
            <Typography className="text" weight="700">
              <Translate id="ARTIST_PROFILE.MENU.AVATAR_EDITOR.DRAGGING" />
            </Typography>
          </Flex>
        ) : null}
      </DropFilesWrapper>
      {props.hasError ? (
        <ErrorWrapper>
          <Typography>
            <Translate id="ARTIST_PROFILE.MENU.AVATAR_EDITOR.ERROR" />
          </Typography>
        </ErrorWrapper>
      ) : null}
    </div>
  );
};

export default connect(mapStateToProps)(NewAvatarEditor);

const DropFilesWrapper = styled(Flex)<{ avatarSize?: string }>`
  position: relative;
  padding: 15px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.box};
  flex-direction: column;

  ${({ theme }) => `@media screen and ${theme.device.laptopL}`} {
    padding: 20px;
  }

  .wrapper {
    justify-content: space-between;
    align-items: center;

    .title {
      margin-bottom: 10px;

      ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
        margin-bottom: 0;
      }
    }

    .subtitle {
      margin-top: 3px;
      color: ${({ theme }) => theme.colors.inputLabel};
    }
  }

  .loading-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${({ avatarSize }) => (avatarSize ? `${avatarSize}px` : '130px')};
    height: ${({ avatarSize }) => (avatarSize ? `${avatarSize}px` : '130px')};
    border-radius: 50%;

    &.loading {
      background: rgba(255, 255, 255, 0.35);
    }
  }

  .btn-browse {
    position: relative;
    width: 100%;

    .file {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      opacity: 0;
      z-index: 1;
    }

    .file,
    .file::-webkit-file-upload-button {
      cursor: pointer;
    }

    .icn-dots {
      z-index: -1;
      pointer-events: none;
      cursor: pointer;
    }
  }

  &.dragging {
    position: relative;
    .drag {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      cursor: grabbing;

      .text {
        z-index: 4;
        color: ${({ theme }) => theme.colors.background};
      }

      &:before,
      &:after {
        content: '';
        position: absolute;
        border-radius: ${({ theme }) => theme.general.borderRadius};
      }

      &:before {
        top: 30px;
        right: 30px;
        bottom: 30px;
        left: 30px;
        border-radius: 2px;
        border: 1px dashed ${({ theme }) => theme.colors.border};
        z-index: 2;
      }

      &:after {
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: ${({ theme }) => theme.colors.primary};
        opacity: 0.9;
        z-index: 1;
      }
    }
  }

  &.error {
    border-radius: 4px 4px 0 0;
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
`;

const ErrorWrapper = styled.div`
  padding: 10px;
  margin-top: 3px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 0 0 4px 4px;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.accentLight};

  ${({ theme }) => `@media screen and ${theme.device.laptopL}`} {
    padding: 10px 20px;
  }

  span {
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
  }
`;
